import { useState } from 'react'

import { errorMessage } from '@/shared/components/data'
import { Button, Field, Input, Select, Sheet, Switch } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import type { CompanyContact } from '@/shared/types/domain'

import { useCreateContact, useUpdateContact } from '../api'

export interface ContactFormProps {
  companyId: string
  /**
   * El contacto a editar. Sin él, el formulario da de alta.
   *
   * Editar existe sobre todo por el idioma: el alta ya lo elige, pero si se
   * equivocaron el contacto quedaba viendo su portal en un idioma que no habla
   * y **nadie** podía corregirlo —él tampoco, porque el portal es de sólo
   * lectura por diseño—.
   */
  contact?: CompanyContact | null
  open: boolean
  onClose: () => void
}

/**
 * Los idiomas que el sistema sabe hablar.
 *
 * El `locale` es **de la persona**, no de la app: las notificaciones salen en
 * el idioma de quien las recibe (`docs/estado.md`, decisiones tomadas). Por eso
 * se elige al cargar el contacto y no se hereda de quien está mirando.
 */
const LOCALES = [
  { value: 'es', label: 'Español' },
  { value: 'pt', label: 'Português' },
  { value: 'en', label: 'English' },
] as const

/** Alta de contacto. **No** da acceso al portal: eso es una acción aparte. */
export function ContactForm({ companyId, contact, open, onClose }: ContactFormProps) {
  const t = useT()
  const create = useCreateContact(companyId)
  const update = useUpdateContact(companyId)
  const editando = Boolean(contact)
  const guardar = editando ? update : create

  const [name, setName] = useState(contact?.name ?? '')
  const [email, setEmail] = useState(contact?.email ?? '')
  const [roleTitle, setRoleTitle] = useState(contact?.role_title ?? '')
  const [isPrimary, setIsPrimary] = useState(contact?.is_primary ?? false)
  const [locale, setLocale] = useState<string>(contact?.locale ?? 'es')
  const [notes, setNotes] = useState(contact?.notes_internal ?? '')

  if (!open) return null

  const valid = name.trim().length > 0 && /.+@.+\..+/.test(email.trim())

  const submit = () => {
    const datos = {
      name: name.trim(),
      email: email.trim(),
      role_title: roleTitle.trim() || null,
      is_primary: isPrimary,
      locale,
      notes_internal: notes.trim() || null,
    }
    if (contact) update.mutate({ contactId: contact.id, ...datos }, { onSuccess: onClose })
    else create.mutate(datos, { onSuccess: onClose })
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={
        <h2 className="text-base text-text-1">
          {editando ? t('companies.contact.edit') : t('companies.contact.new')}
        </h2>
      }
      footer={
        <div className="flex items-center justify-between gap-2">
          {guardar.isError ? (
            <span role="alert" className="text-2xs" style={{ color: 'hsl(var(--overdue))' }}>
              {errorMessage(guardar.error, t('companies.error.createContact'))}
            </span>
          ) : (
            <span />
          )}
          <span className="flex gap-2">
            <Button onClick={onClose}>{t('common.action.cancel')}</Button>
            <Button variant="primary" disabled={!valid || guardar.isPending} onClick={submit}>
              {guardar.isPending
                ? t('companies.contact.creating')
                : editando
                  ? t('common.action.save')
                  : t('common.action.create')}
            </Button>
          </span>
        </div>
      }
    >
      <form
        className="flex flex-col gap-3"
        onSubmit={(event) => {
          event.preventDefault()
          if (valid) submit()
        }}
      >
        <Field label={t('common.label.name')} htmlFor="contact-name">
          <Input
            id="contact-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            autoFocus
          />
        </Field>

        <Field label={t('common.label.email')} htmlFor="contact-email">
          <Input
            id="contact-email"
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field label={t('companies.contact.roleTitle')} htmlFor="contact-role">
          <Input
            id="contact-role"
            value={roleTitle}
            onChange={(event) => setRoleTitle(event.target.value)}
          />
        </Field>

        <Field
          label={t('companies.contact.locale')}
          htmlFor="contact-locale"
          hint={t('companies.contact.localeHint')}
        >
          <Select
            id="contact-locale"
            value={locale}
            onChange={(event) => setLocale(event.target.value)}
          >
            {LOCALES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('companies.contact.isPrimary')} hint={t('companies.contact.isPrimaryHint')}>
          <Switch
            checked={isPrimary}
            onChange={setIsPrimary}
            label={t('companies.contact.isPrimary')}
          />
        </Field>

        <Field label={t('common.label.notes')} htmlFor="contact-notes">
          <Input
            id="contact-notes"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>
      </form>
    </Sheet>
  )
}
